import { Head, router } from "@inertiajs/react"
import React, { useState, useMemo } from "react"
import type { SocialPost, SocialConnection } from "@/types"
import { useSocialPostChannel } from "@/hooks/use-social-post-channel"
import DashboardLayout from "@/components/dashboard/layout"
import IndexToolbar from "@/components/dashboard/index-toolbar"
import Pagination from "@/components/pagination"
import SocialPostDetailDialog from "@/components/dashboard/social-post-detail-dialog"
import NewPostDropdown from "@/components/dashboard/posts/new-post-dropdown"
import { PLATFORM_LABELS, STATUS_STYLES } from "@/lib/social-post-constants"
import { Badge } from "@/components/ui/badge"
import { toast } from "sonner"
import { track } from "@/lib/analytics"
import { csrfToken } from "@/lib/csrf"

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

const POST_FILTERS = [
  {
    label: "All statuses",
    param: "status",
    options: [
      { value: "pending", label: "Pending" },
      { value: "scheduled", label: "Scheduled" },
      { value: "uploading", label: "Uploading" },
      { value: "published", label: "Published" },
      { value: "failed", label: "Failed" },
    ],
  },
  {
    label: "All platforms",
    param: "platform",
    options: [
      { value: "instagram", label: "Instagram" },
      { value: "facebook", label: "Facebook" },
      { value: "tiktok", label: "TikTok" },
      { value: "youtube", label: "YouTube" },
    ],
  },
]

const POST_SORT_OPTIONS = [
  { value: "created_at", label: "Date created" },
  { value: "published_at", label: "Date published" },
  { value: "platform", label: "Platform" },
]

const COLUMNS = [
  { key: "platform", label: "Platform", sortable: true },
  { key: "caption", label: "Caption", sortable: false },
  { key: "status", label: "Status", sortable: false },
  { key: "created_at", label: "Date", sortable: true },
  { key: "actions", label: "", sortable: false },
] as const

export default function PostsIndex({
  posts,
  pagination,
  filters = {},
  connections = [],
}: {
  posts: SocialPost[]
  pagination: PaginationMeta
  filters: Record<string, string>
  connections?: SocialConnection[]
}) {
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null)

  // Real-time social post status updates via ActionCable + polling fallback
  const inflightPosts = useMemo(
    () => posts.filter((p) => p.status === "pending" || p.status === "uploading"),
    [posts],
  )
  const inflightRenderIds = useMemo(
    () => [...new Set(inflightPosts.map((p) => p.render_id).filter(Boolean) as string[])],
    [inflightPosts],
  )
  useSocialPostChannel(inflightRenderIds, inflightPosts.length > 0)

  const currentSort = filters.sort || "created_at"
  const currentDirection = filters.direction || "desc"

  function handleSort(column: string) {
    const newDirection = currentSort === column && currentDirection === "asc" ? "desc" : "asc"
    const params: Record<string, string> = { ...filters, sort: column, direction: newDirection }
    delete params.page
    router.get("/dashboard/posts", params, { preserveState: true })
  }

  function handleDelete(post: SocialPost) {
    return new Promise<void>((resolve) => {
      router.delete(`/dashboard/posts/${post.id}`, {
        preserveScroll: true,
        onFinish: () => resolve(),
      })
    })
  }

  async function handleRetry(post: SocialPost) {
    try {
      const response = await fetch(
        `/dashboard/posts/${post.id}/retry`,
        {
          method: "POST",
          headers: { "X-CSRF-Token": csrfToken() },
        },
      )

      if (response.ok) {
        track("social_post_retried", { platform: post.platform })
        toast.success("Retrying publish...")
        router.reload()
      } else {
        toast.error("Could not retry")
      }
    } catch {
      toast.error("Something went wrong")
    }
  }

  return (
    <DashboardLayout>
      <Head title="Posts | QuiteRight" />

      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
            Posts
          </h1>
          <p className="mt-1 text-warm-grey">
            Your social media posts across all platforms.
          </p>
        </div>
        <NewPostDropdown connections={connections} />
      </div>

      {pagination.count === 0 && !hasActiveFilters(filters) ? (
        <EmptyState connections={connections} />
      ) : (
        <>
          <IndexToolbar
            filters={POST_FILTERS}
            sortOptions={POST_SORT_OPTIONS}
            currentFilters={filters}
            currentSort={currentSort}
            currentDirection={currentDirection}
            baseUrl="/dashboard/posts"
            count={pagination.count}
            noun="post"
          />

          {posts.length === 0 ? (
            <NoResults />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-stone/15">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-stone/15 bg-stone/5 text-left">
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-warm-grey"
                      >
                        {col.sortable ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 transition-colors hover:text-charcoal"
                            onClick={() => handleSort(col.key)}
                          >
                            {col.label}
                            <SortIcon
                              active={currentSort === col.key}
                              direction={currentSort === col.key ? currentDirection : undefined}
                            />
                          </button>
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <PostRow
                      key={post.id}
                      post={post}
                      onClick={() => setSelectedPost(post)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            pagination={pagination}
            baseUrl="/dashboard/posts"
            params={filters}
          />
        </>
      )}

      <SocialPostDetailDialog
        post={selectedPost}
        open={!!selectedPost}
        onOpenChange={(open) => !open && setSelectedPost(null)}
        onDelete={handleDelete}
        onRetry={handleRetry}
        showVideoLink
      />
    </DashboardLayout>
  )
}

function PostRow({ post, onClick }: { post: SocialPost; onClick: () => void }) {
  const statusStyle = STATUS_STYLES[post.status] || STATUS_STYLES.pending
  const displayDate = smartDate(post)

  return (
    <tr
      className="cursor-pointer border-b border-stone/10 bg-white transition-colors hover:bg-stone/5"
      onClick={onClick}
    >
      <td className="whitespace-nowrap px-4 py-3">
        <div className="flex items-center gap-2">
          <PlatformIcon platform={post.platform} className="size-4 shrink-0" />
          <span className="text-sm font-medium text-charcoal">
            {PLATFORM_LABELS[post.platform] || post.platform}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="max-w-[300px] truncate text-sm text-warm-grey">
          {post.caption || "—"}
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <Badge variant="outline" className={`text-[10px] ${statusStyle.className}`}>
          {post.status === "uploading" && (
            <svg className="mr-1 size-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {statusStyle.label}
        </Badge>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-xs text-warm-grey/60">
        {displayDate}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right">
        {post.status === "published" && post.platform_post_url && (
          <a
            href={post.platform_post_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-red hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            View
            <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </a>
        )}
      </td>
    </tr>
  )
}

function smartDate(post: SocialPost): string {
  const date = post.published_at || post.scheduled_at || post.created_at
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function hasActiveFilters(filters: Record<string, string>): boolean {
  return Object.entries(filters).some(([key, val]) => val && key !== "sort" && key !== "direction")
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg className="size-3 opacity-30" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function EmptyState({ connections }: { connections: SocialConnection[] }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone/60 bg-white px-8 py-16 text-center">
      <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-stone/20">
        <svg className="size-8 text-warm-grey" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
        </svg>
      </div>
      <h2 className="mb-2 font-display text-xl font-semibold text-charcoal">
        No posts yet
      </h2>
      <p className="mb-6 max-w-sm text-warm-grey">
        Publish a video, share a listing, or create a standalone post to get started.
      </p>
      <NewPostDropdown connections={connections} />
    </div>
  )
}

function NoResults() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone/60 bg-white px-8 py-12 text-center">
      <p className="text-sm text-warm-grey">No posts match your filters.</p>
    </div>
  )
}

// --- Platform Icons ---

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#E1306C">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  )
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1A1A1A">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  )
}

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#FF0000">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

const PLATFORM_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  tiktok: TikTokIcon,
  youtube: YouTubeIcon,
}

function PlatformIcon({ platform, className }: { platform: string; className?: string }) {
  const Icon = PLATFORM_ICON_MAP[platform]
  if (Icon) return <Icon className={className} />
  return <span className={`inline-block shrink-0 rounded-full bg-warm-grey ${className}`} />
}
